/**
 * Input — keyboard, pointer and gamepad folded into one frame-stable snapshot, with
 * press edges that survive a frame running zero fixed steps.
 */

export type Action = 'lunge' | 'fire' | 'debug' | 'mode' | 'regen' | 'pause' | 'dive' | 'rise';

const ACTIONS: readonly Action[] = [
  'lunge', 'fire', 'debug', 'mode', 'regen', 'pause', 'dive', 'rise',
];

/** keys the browser would otherwise steal (scroll / focus ring) while the game has focus */
const SWALLOW = new Set(['Space', 'F3', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

const THROTTLE_FWD = ['KeyW', 'ArrowUp'];
const THROTTLE_BACK = ['KeyS', 'ArrowDown'];
const STEER_LEFT = ['KeyA', 'ArrowLeft'];
const STEER_RIGHT = ['KeyD', 'ArrowRight'];

// Standard-mapping indices. A/LB for lunge, RT/X for fire — either hand works.
const PAD_LUNGE = [0, 5];
const PAD_FIRE = [7, 2];
/**
 * How hard the pad turns for a given heading error.
 *
 * Matches the autopilot's, and for the same reason: it is the gain at which a shark arrives on
 * a new heading without visibly overshooting it, and having the two disagree would make taking
 * over from the autopilot feel like the animal changed weight.
 */
const TOUCH_STEER_GAIN = 1.9;

/** radial deadzone; below this the stick is noise, and analog triggers latch at half travel */
const PAD_DEADZONE = 0.18;
const PAD_TRIGGER = 0.5;

function flags(): Record<Action, boolean> {
  return {
    lunge: false, fire: false, debug: false, mode: false,
    regen: false, pause: false, dive: false, rise: false,
  };
}

function isTextTarget(el: Element | null): boolean {
  if (el === null) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (el as HTMLElement).isContentEditable === true;
}

export class Input {
  /**
   * Rudder, -1..1. POSITIVE IS SCREEN-RIGHT (D). Not a world direction — the shark steers
   * like a boat, so holding it turns continuously rather than aiming at a heading.
   */
  steer = 0;
  /** -1..1. Positive drives forward along the current heading; negative is a brake. */
  throttle = 0;

  readonly pointerNdc = { x: 0, y: 0 };
  readonly pointerWorld = { x: 0, z: 0 };

  private readonly target: HTMLElement;
  private readonly bind: Record<Action, string[]> = {
    lunge: ['Space', 'Mouse2'],
    fire: ['Mouse0'],
    /* Two bindings on purpose. Backquote is where every engine puts a console, but it is
       also one of the most layout-variable keys there is — it is not where a US keyboard puts
       it on AZERTY, and some layouts do not emit it at all. F3 is positional and identical
       everywhere, so the overlay is always reachable. */
    debug: ['Backquote', 'F3'],
    /* UNBOUND. This used to be Tab, which swapped into the species gallery — a Phase 1
       turntable that has nothing to do with the game and was one mis-hit away at all times.
       The action is left in place because the lab is still reachable programmatically and
       still useful; it simply has no key on it. */
    mode: [],
    regen: ['KeyG'],
    pause: ['Escape'],
    // Depth. Both sit under the left hand without leaving WASD, and Shift is the one the
    // thumb-and-pinky grip reaches while turning, which is when you actually want to dive.
    dive: ['ShiftLeft', 'ShiftRight', 'KeyE'],
    rise: ['KeyQ', 'KeyR'],
  };

  /** raw codes currently held, keyboard `KeyboardEvent.code` plus pseudo-codes `Mouse0`/`Mouse2` */
  private readonly heldCodes = new Set<string>();
  /** edge raised by an event since the last beginFrame — latched, never sampled directly */
  private readonly pendingPress = flags();
  /** the latched edge for the frame currently in flight; what pressed() reports */
  private readonly framePress = flags();
  /** one-shot latch for consume(); cleared by the consumer, re-armed only by a new press */
  private readonly armed = flags();
  private readonly actionDown = flags();
  private readonly padDown = flags();

  private padX = 0;
  private padZ = 0;
  private moveLen = 0;

  /* Touch. Written from outside by ui/TouchControls, which owns the on-screen stick and
     buttons; this class only folds them into the same snapshot every other source lands in, so
     nothing downstream ever learns there is such a thing as a phone. */
  private touchX = 0;
  private touchZ = 0;
  /** the pad's raw direction, screen space: +x right, +y up. Magnitude is the throttle. */
  private touchVX = 0;
  private touchVY = 0;
  /** the shark's heading, fed back each frame so a screen direction can be resolved into it */
  private viewHeading = 0;
  private readonly touchDown = flags();

  private rect: DOMRect;
  private rectDirty = false;
  private disposed = false;

  constructor(target: HTMLElement) {
    this.target = target;
    this.rect = target.getBoundingClientRect();

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('resize', this.markRectDirty);
    window.addEventListener('scroll', this.markRectDirty, true);
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    target.addEventListener('pointerdown', this.onPointerDown);
    target.addEventListener('contextmenu', this.onContextMenu);
  }

  beginFrame(): void {
    this.pollGamepad();

    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      // Latch here rather than at event time: pressed() must read identically from the
      // first fixed step to the last, whether this frame runs 0 steps or 5.
      this.framePress[a] = this.pendingPress[a];
      this.pendingPress[a] = false;

      let held = this.padDown[a] || this.touchDown[a];
      if (!held) {
        const codes = this.bind[a];
        for (let c = 0; c < codes.length; c++) {
          if (this.heldCodes.has(codes[c])) {
            held = true;
            break;
          }
        }
      }
      this.actionDown[a] = held;
    }

    /* Resolve the pad's pointed direction into the rudder-and-throttle the rest of the game
       speaks. The camera rig has NO YAW — see Camera.compose — so screen-right is world -X and
       screen-up is world +Z, exactly and always. That is what makes "left is always left" a
       fact here rather than an approximation: there is no camera rotation to chase. */
    const tmag = Math.min(1, Math.hypot(this.touchVX, this.touchVY));
    if (tmag > 0.001) {
      // Forward is (sin h, cos h), so a world direction's heading is atan2(x, z).
      const want = Math.atan2(-this.touchVX, this.touchVY);
      let off = want - this.viewHeading;
      while (off > Math.PI) off -= Math.PI * 2;
      while (off < -Math.PI) off += Math.PI * 2;
      /* NEGATED, and this is the sign that had the pad inverted in every direction.
         Player.fixedUpdate applies `-steer * turnRate`, so POSITIVE STEER DECREASES HEADING —
         the rudder convention is the opposite of the angle convention. Driving it with the raw
         error turned the shark away from the thumb, the error grew past 180 degrees, and it
         oscillated: inverted and jittery at the same time, from one missing minus. */
      this.touchX = Math.max(-1, Math.min(1, -off * TOUCH_STEER_GAIN));
      /* Still driving while it turns. A shark holds way through a turn; cutting the throttle
         to zero when the pad points backwards would leave it pirouetting on the spot, which is
         both wrong and slow to get out of. */
      this.touchZ = tmag * (0.42 + 0.58 * Math.max(0, Math.cos(off)));
    } else {
      this.touchX = 0;
      this.touchZ = 0;
    }

    // The two axes are independent here — steering is not normalised against throttle, or
    // turning while accelerating would weaken both.
    let ks = 0;
    let kt = 0;
    if (this.anyHeld(STEER_RIGHT)) ks += 1;
    if (this.anyHeld(STEER_LEFT)) ks -= 1;
    if (this.anyHeld(THROTTLE_FWD)) kt += 1;
    if (this.anyHeld(THROTTLE_BACK)) kt -= 1;

    /* Keyboard wins outright — a resting stick must never dilute a held key. Touch comes next
       and the gamepad last, which only matters in the one odd case where both are connected. */
    this.steer = ks !== 0 ? ks : this.touchX !== 0 ? this.touchX : this.padX;
    this.throttle = kt !== 0 ? kt : this.touchZ !== 0 ? this.touchZ : this.padZ;
    this.moveLen = Math.max(Math.abs(this.steer), Math.abs(this.throttle));
  }

  endFrame(): void {
    for (let i = 0; i < ACTIONS.length; i++) this.framePress[ACTIONS[i]] = false;
  }

  /** true if either axis is being driven — used to decide whether the shark is coasting */
  get hasMove(): boolean {
    return this.moveLen > 0.01;
  }

  isDown(a: Action): boolean {
    return this.actionDown[a];
  }

  pressed(a: Action): boolean {
    return this.framePress[a];
  }

  consume(a: Action): boolean {
    if (!this.armed[a]) return false;
    this.armed[a] = false;
    return true;
  }

  /**
   * The on-screen pad, as a DIRECTION rather than as a rudder. +x is screen-right, +y is
   * screen-up, and the magnitude is how hard.
   *
   * This is the whole difference between the pad and the keyboard, and it is deliberate. A and
   * D are a rudder: hold one and the shark keeps turning, which is right for a keyboard because
   * your hand is not pointing at anything. A thumb on glass IS pointing at something — push it
   * left and you mean left, not "begin rotating anticlockwise" — so pressing up made the shark
   * swim FORWARD, whatever direction forward happened to be, and that reads as broken.
   *
   * Resolving it needs the heading, which is why setViewHeading exists.
   */
  setTouchVector(x: number, y: number): void {
    this.touchVX = x;
    this.touchVY = y;
  }

  /** the shark's current heading, so the pad's screen direction can be turned into a rudder */
  setViewHeading(h: number): void {
    this.viewHeading = h;
  }

  /** an on-screen button. Raises the press edge itself, exactly as the gamepad path does. */
  setTouchAction(a: Action, held: boolean): void {
    if (held && !this.touchDown[a]) {
      this.pendingPress[a] = true;
      this.armed[a] = true;
    }
    this.touchDown[a] = held;
  }

  /** true when the primary pointer cannot hover — a phone or a tablet, not a laptop trackpad */
  static get coarsePointer(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  }

  rebind(a: Action, codes: string[]): void {
    this.bind[a] = codes.slice();
  }

  get bindings(): Readonly<Record<Action, string[]>> {
    return this.bind;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('resize', this.markRectDirty);
    window.removeEventListener('scroll', this.markRectDirty, true);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.target.removeEventListener('pointerdown', this.onPointerDown);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
    this.heldCodes.clear();
  }

  private anyHeld(codes: readonly string[]): boolean {
    for (let i = 0; i < codes.length; i++) if (this.heldCodes.has(codes[i])) return true;
    return false;
  }

  private raise(code: string): void {
    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      const codes = this.bind[a];
      for (let c = 0; c < codes.length; c++) {
        if (codes[c] !== code) continue;
        this.pendingPress[a] = true;
        this.armed[a] = true;
        break;
      }
    }
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const typing = isTextTarget(document.activeElement);
    // Escape is the one action that has to work out of the lab's seed field.
    if (typing && e.code !== 'Escape') return;
    if (!typing && SWALLOW.has(e.code)) e.preventDefault();
    if (e.repeat || this.heldCodes.has(e.code)) return;
    this.heldCodes.add(e.code);
    this.raise(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    // Never gated on focus: a key held while focus moves into a text field would stick down.
    this.heldCodes.delete(e.code);
  };

  private onBlur = (): void => {
    this.heldCodes.clear();
    for (let i = 0; i < ACTIONS.length; i++) {
      this.actionDown[ACTIONS[i]] = false;
      this.touchDown[ACTIONS[i]] = false;
    }
    this.padX = 0;
    this.padZ = 0;
    this.touchX = 0;
    this.touchZ = 0;
    this.touchVX = 0;
    this.touchVY = 0;
  };

  private markRectDirty = (): void => {
    this.rectDirty = true;
  };

  private onPointerMove = (e: PointerEvent): void => {
    this.updatePointer(e);
  };

  private onPointerDown = (e: PointerEvent): void => {
    // clicking the viewport hands the keyboard back to the game
    const active = document.activeElement;
    if (isTextTarget(active) && active instanceof HTMLElement) active.blur();
    this.updatePointer(e);
    const code = 'Mouse' + e.button;
    if (this.heldCodes.has(code)) return;
    this.heldCodes.add(code);
    this.raise(code);
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.heldCodes.delete('Mouse' + e.button);
  };

  private onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private updatePointer(e: PointerEvent): void {
    if (this.rectDirty) {
      this.rect = this.target.getBoundingClientRect();
      this.rectDirty = false;
    }
    const r = this.rect;
    const w = r.width > 0 ? r.width : 1;
    const h = r.height > 0 ? r.height : 1;
    this.pointerNdc.x = ((e.clientX - r.left) / w) * 2 - 1;
    this.pointerNdc.y = 1 - ((e.clientY - r.top) / h) * 2;
  }

  private pollGamepad(): void {
    this.padX = 0;
    this.padZ = 0;

    let pad: Gamepad | null = null;
    if (typeof navigator.getGamepads === 'function') {
      const pads = navigator.getGamepads();
      for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (p !== null && p.connected) {
          pad = p;
          break;
        }
      }
    }

    if (pad === null) {
      this.padDown.lunge = false;
      this.padDown.fire = false;
      return;
    }

    const ax = pad.axes.length > 0 ? pad.axes[0] : 0;
    const ay = pad.axes.length > 1 ? pad.axes[1] : 0;
    const mag = Math.hypot(ax, ay);
    if (mag > PAD_DEADZONE) {
      // radial deadzone then a squared magnitude curve: fine steering near centre,
      // full thrust only at the rim. Direction is taken before the curve so it is undistorted.
      const t = Math.min((mag - PAD_DEADZONE) / (1 - PAD_DEADZONE), 1);
      const r = t * t;
      this.padX = (ax / mag) * r;
      this.padZ = -(ay / mag) * r; // stick up is -Y on the pad, +Z in the world
    }

    this.padButton(pad.buttons, PAD_LUNGE, 'lunge');
    this.padButton(pad.buttons, PAD_FIRE, 'fire');
  }

  /** padDown doubles as the previous-state store, so the rising edge falls out for free */
  private padButton(btns: readonly GamepadButton[], idx: readonly number[], a: Action): void {
    let held = false;
    for (let i = 0; i < idx.length; i++) {
      const j = idx[i];
      if (j >= btns.length) continue;
      const b = btns[j];
      if (b.pressed || b.value > PAD_TRIGGER) {
        held = true;
        break;
      }
    }
    if (held && !this.padDown[a]) {
      this.pendingPress[a] = true;
      this.armed[a] = true;
    }
    this.padDown[a] = held;
  }
}
